import React, { useState } from 'react';
import { api } from '@/lib/api';
import { useEmailValidation } from '@/hooks/useEmailValidation';
import { EnvelopeSimple, Warning, Spinner, PencilSimple } from '@phosphor-icons/react';

export function EmailSection({ user, checkAuth }) {
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const { emailError, emailSuggestion, validating, validateEmail, clear } = useEmailValidation();

  const blocked = emailError && !emailSuggestion;

  const save = async (e) => {
    e.preventDefault();
    if (blocked) return;
    setSaving(true);
    setMessage('');
    try {
      await api.put('/auth/update-email', { new_email: newEmail, password });
      await checkAuth();
      setMessage('Email updated successfully');
      setEditing(false);
      setNewEmail('');
      setPassword('');
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSaving(false);
    }
  };

  const applySuggestion = () => { setNewEmail(emailSuggestion); clear(); };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="email-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-outfit text-lg font-semibold text-[#09090B] flex items-center gap-2"><EnvelopeSimple size={20} /> Email Address</h2>
        {!editing && (
          <button onClick={() => setEditing(true)} data-testid="change-email-button"
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-[#002FA7] font-ibm font-semibold rounded-md hover:bg-[#002FA7]/5 transition-all">
            <PencilSimple size={14} /> Change
          </button>
        )}
      </div>
      {message && <div className={`mb-4 p-3 rounded-md text-sm font-ibm ${message.includes('success') ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>{message}</div>}
      {editing ? (
        <form onSubmit={save} className="space-y-4">
          <div>
            <p className="text-xs text-[#6B7280] font-ibm mb-3">Current email: <strong>{user?.email}</strong></p>
          </div>
          <div>
            <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] mb-1 font-ibm">New Email</label>
            <input data-testid="new-email-input" type="email" value={newEmail}
              onChange={e => { setNewEmail(e.target.value); clear(); }}
              onBlur={() => validateEmail(newEmail)}
              required placeholder="newemail@example.com"
              className={`w-full px-3 py-2.5 border rounded-md font-ibm text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${emailError ? 'border-red-300' : 'border-[#E5E7EB]'}`} />
            {emailError && (
              <div className="mt-1.5 flex items-start gap-2">
                <Warning size={14} className="text-orange-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-orange-600 font-ibm" data-testid="email-validation-message">{emailError}</p>
                  {emailSuggestion && (
                    <button type="button" onClick={applySuggestion} data-testid="apply-email-suggestion"
                      className="text-xs text-[#002FA7] font-ibm font-semibold hover:underline mt-0.5">
                      Use {emailSuggestion}
                    </button>
                  )}
                </div>
              </div>
            )}
            {validating && <p className="text-xs text-[#6B7280] font-ibm mt-1"><Spinner size={12} className="animate-spin inline mr-1" /> Validating...</p>}
          </div>
          <div>
            <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] mb-1 font-ibm">Confirm with Password</label>
            <input data-testid="email-change-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Enter current password"
              className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-md font-ibm text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setEditing(false); setNewEmail(''); setPassword(''); setMessage(''); clear(); }} data-testid="cancel-email-button"
              className="px-4 py-2 border border-[#E5E7EB] text-[#09090B] font-ibm text-sm rounded-md hover:bg-[#F3F4F6]">Cancel</button>
            <button data-testid="save-email-button" type="submit" disabled={saving || blocked}
              className="px-4 py-2 bg-[#002FA7] text-white font-ibm text-sm font-semibold rounded-md hover:bg-[#00237D] disabled:opacity-50 transition-all">
              {saving ? 'Updating...' : 'Update Email'}
            </button>
          </div>
        </form>
      ) : (
        <div>
          <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm mb-1">Email</p>
          <p className="font-ibm text-[#09090B]">{user?.email}</p>
        </div>
      )}
    </div>
  );
}
